import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
// Global design system first: component and page styles imported below override it.
import './App.css';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import ContactUsButton from './components/ContactUsButton';
import Home from './pages/Home';
import About from './pages/About';
import Products from './pages/Products';
import Contact from './pages/Contact';
import Partners from './pages/Partners';
import Team from './pages/Team';
import PolicyPage from './pages/PolicyPage';
import ProductCategory from './pages/ProductCategory';
import DeliveryAreas from './pages/DeliveryAreas';
import AreaPage from './pages/AreaPage';
import NotFound from './pages/NotFound';
import usePageTracking from './hooks/usePageTracking';
import { initClickTracking } from './utils/analytics';
import { company } from './data/company';
import { brands } from './data/brands';
import { productPages, productPagePath, PRODUCT_BASE_PATH } from './data/productPages';
import { AREA_BASE_PATH, locations } from './data/locations';
import { SITE_URL as DEFAULT_SITE_URL, BUSINESS_ID, WEBSITE_ID } from './data/seo';

const SITE_URL = DEFAULT_SITE_URL;
const LOGO_URL = `${SITE_URL}/ojanna-logo-square.png`;

// One business entity, referenced by @id from other pages' markup (e.g. Service.provider).
// Only include facts that are visible on the pages carrying this markup.
const businessData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'HardwareStore',
      '@id': BUSINESS_ID,
      name: company.name,
      legalName: company.legalName,
      alternateName: company.alternateNames,
      slogan: company.tagline,
      description: company.description_long,
      url: `${SITE_URL}/`,
      logo: { '@type': 'ImageObject', url: LOGO_URL, width: 512, height: 512 },
      image: [`${SITE_URL}/new_ojanna_front_face.jpg`, LOGO_URL],
      telephone: company.phones[0].international,
      email: company.email,
      contactPoint: company.phones.map((phone) => ({
        '@type': 'ContactPoint',
        telephone: phone.international,
        contactType: phone.label === 'Accounts' ? 'billing support' : 'sales',
        areaServed: 'ZA',
        availableLanguage: ['English', 'Afrikaans'],
      })),
      address: {
        '@type': 'PostalAddress',
        streetAddress: company.address.street,
        addressLocality: company.address.locality,
        addressRegion: company.address.region,
        addressCountry: company.address.country,
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: company.geo.latitude,
        longitude: company.geo.longitude,
      },
      hasMap: company.mapsUrl,
      openingHoursSpecification: company.openingHours.map((h) => ({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: h.days,
        opens: `${h.opens}:00`,
        closes: `${h.closes}:00`,
      })),
      founder: {
        '@type': 'Person',
        name: company.founder.name,
        jobTitle: company.founder.jobTitle,
        description: company.founder.description,
      },
      knowsAbout: company.knowledgeAreas,
      brand: brands.map((b) => ({ '@type': 'Brand', name: b.name })),
      memberOf: {
        '@type': 'Organization',
        name: 'SABISA — South African Building Interior Systems Association',
      },
      priceRange: company.priceRange,
      currenciesAccepted: 'ZAR',
      areaServed: [
        { '@type': 'City', name: 'George' },
        ...locations.map((l) => ({ '@type': 'Place', name: l.name.replace(/^the /, '') })),
        ...company.areasServed.regions.map((name) => ({ '@type': 'AdministrativeArea', name })),
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Ceiling, drywall, insulation and acoustic supplies',
        itemListElement: productPages.map((page) => ({
          '@type': 'OfferCatalog',
          name: page.name,
          url: `${SITE_URL}${productPagePath(page)}`,
        })),
      },
      sameAs: company.sameAs,
    },
    {
      '@type': 'WebSite',
      '@id': WEBSITE_ID,
      url: `${SITE_URL}/`,
      name: company.name,
      alternateName: company.alternateNames,
      publisher: { '@id': BUSINESS_ID },
      inLanguage: 'en-ZA',
    },
  ],
};

// Pages that describe the business itself carry the full business markup.
const BUSINESS_SCHEMA_PATHS = ['/', '/about', '/contact'];

export function AppRoutes() {
  usePageTracking();
  const { pathname } = useLocation();

  // GA4 click tracking for call / WhatsApp / email / directions / maps / quote links
  useEffect(() => initClickTracking(), []);

  return (
    <div className="App">
      <Helmet>
        <html lang="en-ZA" />
        {BUSINESS_SCHEMA_PATHS.includes(pathname) && (
          <script type="application/ld+json">{JSON.stringify(businessData)}</script>
        )}
      </Helmet>
      <Navigation />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/products" element={<Products />} />
          <Route path={`${PRODUCT_BASE_PATH}/:slug`} element={<ProductCategory />} />
          <Route path="/delivery-areas" element={<DeliveryAreas />} />
          <Route path={`${AREA_BASE_PATH}/:slug`} element={<AreaPage />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/team" element={<Team />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<PolicyPage />} />
          <Route path="/cookie-policy" element={<PolicyPage />} />
          <Route path="/terms-of-use" element={<PolicyPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <CookieConsent />
      <ContactUsButton />
    </div>
  );
}

// The router is supplied by the caller: BrowserRouter in src/index.js,
// StaticRouter in src/prerender.js.
export default AppRoutes;
